import { useEffect, useState } from "react";
import LibrarySearchBar from "../../components/LibrarySearchBar/LibrarySearchBar";
import Typography from "../../components/Typography/Typography";
import BaseAppLayout from "../../layouts/BaseAppLayout/BaseAppLayout";
import LatestSearchBox from "./LatestSearchBox";
import { useAuth } from "../../store/AuthContext/AuthContext";
import ApiManager from "../../api/ApiManager";

export default function ResearchChats() {
  const [searchValue, setSearchValue] = useState("");
  const [searchData, setSearchData] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [chatsCount, setChatsCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const { getCookie } = useAuth();
  const projectId = getCookie("project_id");
  const id_token = getCookie("id_token");

  const handleSearchChange = (value) => {
    setSearchValue(value);
  };

  const handleClickSearch = (query) => {
    // No additional action needed as we're already filtering in real-time
  };

  const getChats = () => {
    const api = new ApiManager();

    api
      .get(`/search/get?project_id=${projectId}`, {
        headers: { Authorization: `Bearer ${id_token}` },
      })
      .then((response) => {
        if (response.data && response.data.history) {
          try {
            const parsedBody =
              typeof response.data.history === "string"
                ? JSON.parse(response.data.history)
                : response.data.history;

            setSearchData(parsedBody);
            setFilteredHistory(parsedBody); // Initialize filtered history with all chats

            if (Array.isArray(parsedBody)) {
              setChatsCount(parsedBody.length);
            }
          } catch (error) {
            console.error("Error parsing response body:", error);
            setSearchData([]);
            setFilteredHistory([]);
          }
        } else {
          console.error("Response body is missing or malformed.");
          setSearchData([]);
          setFilteredHistory([]);
        }
      })
      .catch((error) => {
        console.error("API call error:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Fetch data when projectId is available
  useEffect(() => {
    if (projectId) {
      getChats();
    }
  }, [projectId]);

  // Filter chats based on search value
  useEffect(() => {

    if (searchData && Array.isArray(searchData)) {
      const filtered = searchData.filter((item) => {
        const titleMatches = item.title
          ?.toLowerCase()
          .includes(searchValue.toLowerCase());
        return titleMatches;
      });

      setFilteredHistory(filtered);
    } else {
      setFilteredHistory([]);
    }
  }, [searchValue, searchData]);

  const handleDeleteChat = (id) => {
    // Remove chat from the list after deletion
    setSearchData((prevData) => prevData.filter((chat) => chat.chat_id !== id));
    setChatsCount((prevCount) => prevCount - 1);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col gap-3">
          <div className="w-full h-24 bg-gray-100 animate-pulse rounded-[12px]" />
          <div className="w-full h-24 bg-gray-100 animate-pulse rounded-[12px]" />
          <div className="w-full h-24 bg-gray-100 animate-pulse rounded-[12px]" />
          <div className="w-full h-24 bg-gray-100 animate-pulse rounded-[12px]" />
          <div className="w-full h-24 bg-gray-100 animate-pulse rounded-[12px]" />
          <div className="w-full h-24 bg-gray-100 animate-pulse rounded-[12px]" />
        </div>
      );
    } else if (!loading && searchValue && filteredHistory.length === 0) {
      return (
        <div className="flex flex-col gap-4">
          <img
            src="/assets/images/illustrations/treasure_info.png"
            alt="Treasure Chest"
            className="w-64 mx-auto"
          />
          <Typography size="16" weight="rg" align="center">
            Non abbiamo trovato chat con "{searchValue}"
          </Typography>
          <Typography size="16" weight="rg" align="center">
            (╥﹏╥)
          </Typography>
        </div>
      );
    } else if (!loading && !searchValue && chatsCount === 0) {
      return (
        <div className="flex flex-col gap-4">
          <img
            src="/assets/images/illustrations/treasure_info.png"
            alt="Treasure Chest"
            className="w-64 mx-auto"
          />
          <Typography size="16" weight="rg" align="center">
            Non hai ancora chat salvate.
          </Typography>
          <Typography size="16" weight="rg" align="center">
            (╥﹏╥)
          </Typography>
        </div>
      );
    } else {
      // Always use filteredHistory for rendering
      return (
        <>
          <Typography size="12" weight="rg" align="left">
            Hai
            <span className="mx-1">
              <Typography size="12" weight="sb">
                {filteredHistory.length}
              </Typography>
            </span>
            chat con Athena
          </Typography>
          {filteredHistory
            .sort(
              (a, b) => new Date(b.creation_date) - new Date(a.creation_date)
            )
            .map((item, index) => (
              <LatestSearchBox
                key={item.id || index}
                title={item.title}
                time={item.creation_date}
                id={item.chat_id}
                hasDelete={true}
                onDelete={handleDeleteChat}
              />
            ))}
        </>
      );
    }
  };

  return (
    <BaseAppLayout headerText="Chats">
      <div className="flex flex-col md:gap-3 gap-6 px-6 md:mx-auto md:w-[40rem] w-auto pt-10 pb-12 md:pb-20">
        <Typography size="32" desktopSize="48" weight="sb" family="serif">
          Le tue ricerche
        </Typography>
        <div className="flex flex-col gap-3">
          <LibrarySearchBar
            placeholder="Cerca tra le tue chat"
            fullWidth
            value={searchValue}
            onChange={handleSearchChange}
            onSearch={handleClickSearch}
          />
        </div>
        <div className="flex flex-col gap-3">{renderContent()}</div>
      </div>
    </BaseAppLayout>
  );
}
